import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenantContext } from '../../auth/useTenantContext';
import {
  useAssignmentBoard, useAssignWhole, useAssignOperator,
  useCancelWhole, useUnassignOperator, useReorder,
} from '../../api/assignments';
import { useUnassignMission } from '../../api/pickListLifecycle';
import { AssignmentBoard, computePlacement } from '../../components/assignments/AssignmentBoard';
import { PageHeading } from '../../layouts/PageHeading';

export default function AssignmentsBoardPage() {
  const { t } = useTranslation(['assignment', 'shell']);
  const { token, tenantCode, warehouseId } = useTenantContext();
  // SP-6e fix: read the active warehouse from context (written by the header
  // WarehouseSelector). Previously this was hardcoded to 1 ("MVP fallback"),
  // which 403'd on every deployment whose warehouse id ≠ 1 → "Impossibile
  // caricare la board". The board hooks gate on warehouseId !== null.
  const board = useAssignmentBoard(token, tenantCode, warehouseId);
  const ctx = { warehouseId: warehouseId ?? 0 };
  const assignWhole      = useAssignWhole(token, tenantCode, ctx);
  const assignOperator   = useAssignOperator(token, tenantCode, ctx);
  const cancelWhole      = useCancelWhole(token, tenantCode, ctx);
  const unassignOperator = useUnassignOperator(token, tenantCode, ctx);
  const reorder          = useReorder(token, tenantCode, ctx);
  const unassignMission  = useUnassignMission(token, tenantCode, ctx.warehouseId);
  const [splitForPlId, setSplitForPlId] = useState<number | null>(null);

  if (warehouseId === null) {
    return <p>{t('assignment:page.selectWarehouse')}</p>;
  }
  if (board.isLoading) return <p>{t('assignment:page.loading')}</p>;
  if (board.isError || !board.data) return <p>{t('assignment:page.errorLoading')}</p>;

  // SP-UX — "inserisci nel punto esatto": dopo aver assegnato (append), riordina
  // la coda dell'operatore inserendo la nuova missione prima di `beforeMissionId`
  // (= posizione di rilascio → priorità claim_order). Usa lo stato pre-assegna
  // (board.data nel render corrente) + la nuova mission id, in un secondo step.
  const placeAt = async (
    operatorUserId: number,
    newMissionId: number | undefined,
    beforeMissionId: number | undefined,
  ): Promise<void> => {
    if (!board.data) return;
    const ids = computePlacement(board.data, operatorUserId, newMissionId, beforeMissionId);
    if (!ids) return;
    await reorder.mutateAsync({ operatorUserId, missionIds: ids });
  };

  return (
    <div>
      <PageHeading
        scope={t('shell:groups.operations')}
        target={t('shell:menu.assignments')}
        meta={t('assignment:page.meta')}
      />
      <AssignmentBoard
        board={board.data}
        callbacks={{
          onAssignWhole:      async (v) => {
            const res = await assignWhole.mutateAsync({ pickListId: v.pickListId, operatorUserId: v.operatorUserId });
            const newMissionId = (res as { mission_id?: number } | null)?.mission_id;
            await placeAt(v.operatorUserId, newMissionId, v.beforeMissionId);
            return newMissionId; // flashed green by the board
          },
          onAssignOperator:   async (v) => {
            await assignOperator.mutateAsync({ missionId: v.missionId, operatorUserId: v.operatorUserId });
            await placeAt(v.operatorUserId, v.missionId, v.beforeMissionId);
          },
          onCancelWhole:      (v) => cancelWhole.mutate(v),
          onUnassignOperator: (v) => unassignOperator.mutate(v),
          onReorder:          (v) => reorder.mutate(v),
          onOpenSplitWizard:  (id) => setSplitForPlId(id),
          onUnassignMission:  (v) => unassignMission.mutate(v),
        }}
      />
      {splitForPlId !== null && (
        <p style={{ marginTop: 16 }}>
          <a href={`/pick-lists/${splitForPlId}`}>
            {t('assignment:page.goToPickListDetail')}
          </a>
        </p>
      )}
    </div>
  );
}
